import React, { useEffect, useRef } from 'react';
import '../styles/AnimatedBackground.css';

interface Node {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  parallax: number;
  pulsePhase: number;
  pulseSpeed: number;
}

interface Orb {
  x: number;
  y: number;
  radius: number;
  hue: string;
  parallax: number;
}

const AnimatedBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollY = useRef<number>(0);
  const rafId = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = window.innerWidth;
    let height = document.documentElement.scrollHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      width = window.innerWidth;
      height = document.documentElement.scrollHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + 'px';
      canvas.style.height = height + 'px';
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();

    const nodeCount = Math.min(60, Math.floor((width * Math.min(height, 3000)) / 40000));
    const nodes: Node[] = [];
    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.15,
        vy: (Math.random() - 0.5) * 0.15,
        radius: Math.random() * 1.2 + 0.6,
        parallax: Math.random() * 0.25 + 0.15,
        pulsePhase: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.015 + 0.008,
      });
    }

    const orbs: Orb[] = [
      { x: width * 0.15, y: height * 0.08, radius: 260, hue: 'rgba(91, 140, 255, 0.08)', parallax: 0.1 },
      { x: width * 0.85, y: height * 0.25, radius: 300, hue: 'rgba(167, 139, 250, 0.07)', parallax: 0.08 },
      { x: width * 0.25, y: height * 0.55, radius: 240, hue: 'rgba(34, 211, 238, 0.05)', parallax: 0.06 },
      { x: width * 0.75, y: height * 0.85, radius: 280, hue: 'rgba(91, 140, 255, 0.06)', parallax: 0.09 },
    ];

    const onScroll = () => {
      scrollY.current = window.pageYOffset;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', resize);

    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      const vpY = window.pageYOffset;
      const vpH = window.innerHeight;

      // Orbs
      orbs.forEach((orb) => {
        const y = orb.y - scrollY.current * orb.parallax;
        if (y > vpY - 500 && y < vpY + vpH + 500) {
          const g = ctx.createRadialGradient(orb.x, y, 0, orb.x, y, orb.radius);
          g.addColorStop(0, orb.hue);
          g.addColorStop(1, 'rgba(0,0,0,0)');
          ctx.fillStyle = g;
          ctx.fillRect(orb.x - orb.radius, y - orb.radius, orb.radius * 2, orb.radius * 2);
        }
      });

      // Nodes + connections
      const visibleNodes: { x: number; y: number; r: number; glow: number }[] = [];
      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0) n.x = width;
        if (n.x > width) n.x = 0;
        if (n.y < 0) n.y = height;
        if (n.y > height) n.y = 0;

        n.pulsePhase += n.pulseSpeed;
        const pulse = 0.5 + (Math.sin(n.pulsePhase) + 1) * 0.25; // 0.5–1.0
        const y = n.y - scrollY.current * n.parallax;

        if (y > vpY - 100 && y < vpY + vpH + 100) {
          visibleNodes.push({ x: n.x, y, r: n.radius, glow: pulse });
          ctx.fillStyle = `rgba(180, 200, 255, ${0.35 * pulse})`;
          ctx.beginPath();
          ctx.arc(n.x, y, n.radius, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      for (let i = 0; i < visibleNodes.length; i++) {
        const a = visibleNodes[i];
        for (let j = i + 1; j < visibleNodes.length; j++) {
          const b = visibleNodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 140) {
            const alpha = (1 - d / 140) * 0.18;
            ctx.strokeStyle = `rgba(120, 160, 255, ${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      rafId.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      if (rafId.current) cancelAnimationFrame(rafId.current);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="animated-background" aria-hidden="true" />;
};

export default AnimatedBackground;
